// SPDX-License-Identifier: AGPL-3.0-only

import {
  WebGLShallowWaterSolver,
  exportDepthPoints,
  exportSimulationReport,
  exportWetExtent,
  type DemGrid,
  type InflowSource,
  type SimulationReportContext,
  type SimulationSnapshot,
  type SimulationSourceRecord,
  type SolverParameters
} from '@openfloodlab/simulation-core';
import maplibregl, { type CustomLayerInterface, type Map as MapLibreMap } from 'maplibre-gl';
import { TERRAIN_FRAGMENT_SHADER, TERRAIN_VERTEX_SHADER } from './shaders';

export interface OpenFloodLayerOptions {
  readonly id?: string;
  readonly grid: DemGrid;
  readonly minimumElevationMeters: number;
  readonly maximumElevationMeters: number;
  readonly solverParameters?: Partial<SolverParameters>;
  readonly waterThresholdMeters?: number;
  readonly waterHeightScale?: number;
  readonly simulationSpeed?: number;
  /** @deprecated Use simulationSpeed. */
  readonly stepsPerFrame?: number;
  readonly reportContext?: Pick<SimulationReportContext, 'dem' | 'software'>;
}

interface MutableSimulationSourceRecord extends InflowSource {
  startedAtModelSeconds: number;
  stoppedAtModelSeconds: number | null;
  injectedVolumeCubicMeters: number;
}

export class OpenFloodLayer implements CustomLayerInterface {
  readonly id: string;
  readonly type = 'custom' as const;
  readonly renderingMode = '3d' as const;
  readonly grid: DemGrid;

  private readonly options: OpenFloodLayerOptions;
  private readonly origin: readonly [number, number, number];
  private map: MapLibreMap | null = null;
  private gl: WebGL2RenderingContext | null = null;
  private solver: WebGLShallowWaterSolver | null = null;
  private program: WebGLProgram | null = null;
  private vertexArray: WebGLVertexArrayObject | null = null;
  private vertexBuffer: WebGLBuffer | null = null;
  private indexBuffer: WebGLBuffer | null = null;
  private indexCount = 0;
  private paused = false;
  private simulationStarted = false;
  private sources: InflowSource[] = [];
  private sourceMarkers = new Map<string, HTMLButtonElement>();
  private sourceHistory: MutableSimulationSourceRecord[] = [];
  private simulationSpeed: number;
  private waterThresholdMeters: number;
  private waterHeightScale: number;
  private readonly projectionMatrix = new Float32Array(16);
  private hasProjectionMatrix = false;

  constructor(options: OpenFloodLayerOptions) {
    this.options = options;
    this.id = options.id ?? 'openfloodlab-simulation';
    this.grid = options.grid;
    const [west, south, east, north] = options.grid.bounds;
    const origin = maplibregl.MercatorCoordinate.fromLngLat(
      [(west + east) * 0.5, (south + north) * 0.5],
      0
    );
    this.origin = [origin.x, origin.y, origin.z];
    this.simulationSpeed = options.simulationSpeed ?? options.stepsPerFrame ?? 10;
    this.waterThresholdMeters = options.waterThresholdMeters ?? 0.01;
    this.waterHeightScale = options.waterHeightScale ?? 1.6;
  }

  onAdd(map: MapLibreMap, context: WebGLRenderingContext): void {
    if (!(context instanceof WebGL2RenderingContext)) {
      throw new Error('OpenFloodLab requires a WebGL2 map context.');
    }
    this.map = map;
    this.gl = context;
    this.solver = new WebGLShallowWaterSolver(context, this.grid, this.options.solverParameters);
    this.solver.setSources(this.sources);
    this.program = createProgram(context, TERRAIN_VERTEX_SHADER, TERRAIN_FRAGMENT_SHADER);
    this.buildTerrainMesh(context);
    this.syncSourceMarkers();
  }

  render(context: WebGLRenderingContext, renderContext: unknown): void {
    const gl = context as WebGL2RenderingContext;
    if (!this.solver || !this.program || !this.vertexArray) return;
    if (!this.paused && this.simulationStarted) {
      const substeps = Math.max(3, Math.min(8, Math.ceil(this.simulationSpeed / 12.5)));
      const dt = (this.solver.stableTimeStep() * this.simulationSpeed) / substeps;
      this.solver.step(substeps, dt);
    }

    const matrix = resolveMainMatrix(renderContext);
    this.projectionMatrix.set(matrix);
    this.hasProjectionMatrix = true;
    this.updateSourceMarkers(matrix);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vertexArray);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.solver.currentStateTexture);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_state'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.solver.currentTerrainTexture);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_terrain'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.solver.currentFluxTexture);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_flux'), 2);
    gl.uniformMatrix4fv(gl.getUniformLocation(this.program, 'u_matrix'), false, matrix);
    const [centerClip, centerClipLow] = projectOrigin(matrix, this.origin);
    gl.uniform4fv(gl.getUniformLocation(this.program, 'u_center_clip'), centerClip);
    gl.uniform4fv(gl.getUniformLocation(this.program, 'u_center_clip_low'), centerClipLow);
    gl.uniform2f(
      gl.getUniformLocation(this.program, 'u_resolution'),
      this.grid.width,
      this.grid.height
    );
    gl.uniform2f(
      gl.getUniformLocation(this.program, 'u_cell_size'),
      this.solver.metrics.cellWidthMeters,
      this.solver.metrics.cellHeightMeters
    );
    const camera = resolveCameraPosition(this.map);
    gl.uniform3f(
      gl.getUniformLocation(this.program, 'u_camera_position'),
      camera[0] - this.origin[0],
      camera[1] - this.origin[1],
      camera[2] - this.origin[2]
    );
    gl.uniform1f(
      gl.getUniformLocation(this.program, 'u_min_elevation'),
      this.options.minimumElevationMeters
    );
    gl.uniform1f(
      gl.getUniformLocation(this.program, 'u_max_elevation'),
      this.options.maximumElevationMeters
    );
    gl.uniform1f(
      gl.getUniformLocation(this.program, 'u_water_height_scale'),
      this.waterHeightScale
    );

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.depthMask(true);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_render_water'), 0);
    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_INT, 0);

    gl.depthMask(false);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_render_water'), 1);
    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_INT, 0);

    gl.depthMask(true);
    gl.bindVertexArray(null);
    for (let unit = 2; unit >= 0; unit -= 1) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, null);
    }
    gl.useProgram(null);
    if (!this.paused && this.simulationStarted) this.map?.triggerRepaint();
  }

  onRemove(): void {
    this.solver?.destroy();
    if (this.gl) {
      if (this.program) this.gl.deleteProgram(this.program);
      if (this.vertexArray) this.gl.deleteVertexArray(this.vertexArray);
      if (this.vertexBuffer) this.gl.deleteBuffer(this.vertexBuffer);
      if (this.indexBuffer) this.gl.deleteBuffer(this.indexBuffer);
    }
    this.solver = null;
    for (const marker of this.sourceMarkers.values()) marker.remove();
    this.sourceMarkers.clear();
    this.map = null;
    this.gl = null;
  }

  setSources(sources: readonly InflowSource[]): void {
    const modelTime = this.solver?.modelTimeSeconds ?? 0;
    const nextById = new Map(sources.map((source) => [source.id, source]));
    for (const record of this.sourceHistory) {
      if (record.stoppedAtModelSeconds !== null) continue;
      const next = nextById.get(record.id);
      if (!next || !sameSource(record, next)) {
        record.stoppedAtModelSeconds = modelTime;
        record.injectedVolumeCubicMeters = Math.max(
          0,
          (modelTime - record.startedAtModelSeconds) * record.flowRateCubicMetersPerSecond
        );
      }
    }
    for (const source of sources) {
      const active = this.sourceHistory.find(
        (record) => record.id === source.id && record.stoppedAtModelSeconds === null
      );
      if (!active) {
        this.sourceHistory.push({
          ...source,
          startedAtModelSeconds: modelTime,
          stoppedAtModelSeconds: null,
          injectedVolumeCubicMeters: 0
        });
      }
    }
    this.sources = [...sources];
    this.syncSourceMarkers();
    if (this.sources.length > 0) this.simulationStarted = true;
    this.solver?.setSources(this.sources);
    this.map?.triggerRepaint();
  }

  getSources(): readonly InflowSource[] {
    return this.sources;
  }

  getActiveDomain() {
    return this.solver?.activeDomain ?? null;
  }

  getTerrainScreenOffset(longitude: number, latitude: number): readonly [number, number] {
    const canvas = this.map?.getCanvas();
    if (!canvas || !this.hasProjectionMatrix) return [0, 0];
    const elevation = sampleGridElevation(this.grid, longitude, latitude);
    const ground = maplibregl.MercatorCoordinate.fromLngLat([longitude, latitude], 0);
    const terrain = maplibregl.MercatorCoordinate.fromLngLat(
      [longitude, latitude],
      Math.max(0, elevation - this.options.minimumElevationMeters)
    );
    const groundPoint = projectMercatorPoint(this.projectionMatrix, canvas, ground);
    const terrainPoint = projectMercatorPoint(this.projectionMatrix, canvas, terrain);
    if (!groundPoint || !terrainPoint) return [0, 0];
    return [terrainPoint[0] - groundPoint[0], terrainPoint[1] - groundPoint[1]];
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    this.map?.triggerRepaint();
  }

  isPaused(): boolean {
    return this.paused;
  }

  setStepsPerFrame(value: number): void {
    this.setSimulationSpeed(value);
  }

  setSimulationSpeed(value: number): void {
    this.simulationSpeed = Math.max(0.1, Math.min(100, Number(value) || 1));
  }

  setWaterThresholdMeters(value: number): void {
    this.waterThresholdMeters = Math.max(0.001, Number(value) || 0.01);
    this.map?.triggerRepaint();
  }

  setWaterHeightScale(value: number): void {
    this.waterHeightScale = Math.max(0.1, Math.min(10, Number(value) || 1));
    this.map?.triggerRepaint();
  }

  reset(): void {
    this.solver?.reset();
    this.simulationStarted = this.sources.length > 0;
    this.sourceHistory = this.sources.map((source) => ({
      ...source,
      startedAtModelSeconds: 0,
      stoppedAtModelSeconds: null,
      injectedVolumeCubicMeters: 0
    }));
    this.map?.triggerRepaint();
  }

  advance(iterations = 1, requestedTimeStepSeconds?: number): number {
    if (!this.solver) throw new Error('Simulation layer is not attached to a map.');
    this.simulationStarted = true;
    const elapsed = this.solver.step(iterations, requestedTimeStepSeconds);
    this.map?.triggerRepaint();
    return elapsed;
  }

  snapshot(): SimulationSnapshot {
    if (!this.solver) throw new Error('Simulation layer is not attached to a map.');
    return this.solver.snapshot();
  }

  exportDepth(thresholdMeters = this.waterThresholdMeters) {
    return exportDepthPoints(this.grid, this.snapshot().depth, { thresholdMeters });
  }

  exportExtent(thresholdMeters = this.waterThresholdMeters) {
    return exportWetExtent(this.grid, this.snapshot().depth, thresholdMeters);
  }

  exportReport(thresholdMeters = this.waterThresholdMeters) {
    if (!this.solver) throw new Error('Simulation layer is not attached to a map.');
    const snapshot = this.snapshot();
    return exportSimulationReport(this.grid, snapshot.statistics, thresholdMeters, {
      solverImplementation: 'WebGLShallowWaterSolver',
      solverParameters: this.solver.parameters,
      sourceHistory: this.getSourceHistory(this.solver.modelTimeSeconds),
      ...this.options.reportContext
    });
  }

  pickDemPosition(screenX: number, screenY: number): readonly [number, number] | null {
    const canvas = this.map?.getCanvas();
    if (!canvas || !this.hasProjectionMatrix) return null;

    const stride = Math.max(1, Math.floor(Math.max(this.grid.width, this.grid.height) / 48));
    let bestX = 0;
    let bestY = 0;
    let bestDistanceSquared = Number.POSITIVE_INFINITY;
    const test = (x: number, y: number) => {
      const projected = projectGridPoint(
        this.grid,
        this.options.minimumElevationMeters,
        this.projectionMatrix,
        canvas,
        x,
        y
      );
      if (!projected) return;
      const dx = projected[0] - screenX;
      const dy = projected[1] - screenY;
      const distanceSquared = dx * dx + dy * dy;
      if (distanceSquared >= bestDistanceSquared) return;
      bestDistanceSquared = distanceSquared;
      bestX = x;
      bestY = y;
    };

    for (let y = 0; y < this.grid.height; y += stride) {
      for (let x = 0; x < this.grid.width; x += stride) test(x, y);
      test(this.grid.width - 1, y);
    }
    for (let x = 0; x < this.grid.width; x += stride) test(x, this.grid.height - 1);
    test(this.grid.width - 1, this.grid.height - 1);

    const coarseX = bestX;
    const coarseY = bestY;
    for (
      let y = Math.max(0, coarseY - stride);
      y <= Math.min(this.grid.height - 1, coarseY + stride);
      y += 1
    ) {
      for (
        let x = Math.max(0, coarseX - stride);
        x <= Math.min(this.grid.width - 1, coarseX + stride);
        x += 1
      ) {
        test(x, y);
      }
    }

    if (bestDistanceSquared > 32 * 32) return null;
    const [west, south, east, north] = this.grid.bounds;
    return [
      west + (bestX / (this.grid.width - 1)) * (east - west),
      north - (bestY / (this.grid.height - 1)) * (north - south)
    ];
  }

  getSourceHistory(modelTime = this.solver?.modelTimeSeconds ?? 0): SimulationSourceRecord[] {
    return this.sourceHistory.map((record) => ({
      ...record,
      injectedVolumeCubicMeters:
        record.stoppedAtModelSeconds === null
          ? Math.max(
              0,
              (modelTime - record.startedAtModelSeconds) * record.flowRateCubicMetersPerSecond
            )
          : record.injectedVolumeCubicMeters
    }));
  }

  private buildTerrainMesh(gl: WebGL2RenderingContext): void {
    const { width, height, elevations, bounds } = this.grid;
    const [west, south, east, north] = bounds;
    const columns = width;
    const rows = height;
    const surfaceVertexCount = (columns + 1) * (rows + 1);
    const skirtSegmentCount = 2 * columns + 2 * rows;
    const vertices = new Float32Array((surfaceVertexCount + skirtSegmentCount * 4) * 6);
    let vertexOffset = 0;

    const appendVertex = (x: number, y: number, elevation: number, u: number, v: number) => {
      const latitudeRadians = (y * Math.PI) / 180;
      const sine = Math.sin(latitudeRadians);
      const mercatorX = (x + 180) / 360;
      const mercatorY = 0.5 - Math.log((1 + sine) / (1 - sine)) / (4 * Math.PI);
      const metersToMercator = 1 / (40_075_016.68557849 * Math.cos(latitudeRadians));
      const mercatorZ =
        Math.max(0, elevation - this.options.minimumElevationMeters) * metersToMercator;
      vertices[vertexOffset++] = mercatorX - this.origin[0];
      vertices[vertexOffset++] = mercatorY - this.origin[1];
      vertices[vertexOffset++] = mercatorZ - this.origin[2];
      vertices[vertexOffset++] = u;
      vertices[vertexOffset++] = v;
      vertices[vertexOffset++] = metersToMercator;
    };

    const sampleElevation = (u: number, v: number) => {
      const gridX = Math.min(width - 1, Math.max(0, u * (width - 1)));
      const gridY = Math.min(height - 1, Math.max(0, v * (height - 1)));
      const x0 = Math.floor(gridX);
      const y0 = Math.floor(gridY);
      const x1 = Math.min(width - 1, x0 + 1);
      const y1 = Math.min(height - 1, y0 + 1);
      const fx = gridX - x0;
      const fy = gridY - y0;
      const top =
        (elevations[y0 * width + x0] ?? 0) * (1 - fx) + (elevations[y0 * width + x1] ?? 0) * fx;
      const bottom =
        (elevations[y1 * width + x0] ?? 0) * (1 - fx) + (elevations[y1 * width + x1] ?? 0) * fx;
      return top * (1 - fy) + bottom * fy;
    };

    for (let y = 0; y <= rows; y += 1) {
      const v = y / rows;
      const latitude = north - v * (north - south);
      for (let x = 0; x <= columns; x += 1) {
        const u = x / columns;
        const longitude = west + u * (east - west);
        appendVertex(longitude, latitude, sampleElevation(u, v), u, v);
      }
    }

    const surfaceIndexCount = columns * rows * 6;
    const indices = new Uint32Array(surfaceIndexCount + skirtSegmentCount * 6);
    let indexOffset = 0;
    for (let y = 0; y < rows; y += 1) {
      for (let x = 0; x < columns; x += 1) {
        const topLeft = y * (columns + 1) + x;
        const topRight = topLeft + 1;
        const bottomLeft = topLeft + columns + 1;
        const bottomRight = bottomLeft + 1;
        indices[indexOffset++] = topLeft;
        indices[indexOffset++] = bottomLeft;
        indices[indexOffset++] = topRight;
        indices[indexOffset++] = topRight;
        indices[indexOffset++] = bottomLeft;
        indices[indexOffset++] = bottomRight;
      }
    }

    let skirtVertex = surfaceVertexCount;
    const appendSkirt = (x0: number, y0: number, x1: number, y1: number) => {
      const u0 = x0 / columns;
      const v0 = y0 / rows;
      const u1 = x1 / columns;
      const v1 = y1 / rows;
      const longitude0 = west + u0 * (east - west);
      const latitude0 = north - v0 * (north - south);
      const longitude1 = west + u1 * (east - west);
      const latitude1 = north - v1 * (north - south);
      appendVertex(longitude0, latitude0, sampleElevation(u0, v0), u0, v0);
      appendVertex(longitude1, latitude1, sampleElevation(u1, v1), u1, v1);
      appendVertex(longitude0, latitude0, this.options.minimumElevationMeters, u0, v0);
      appendVertex(longitude1, latitude1, this.options.minimumElevationMeters, u1, v1);
      indices[indexOffset++] = skirtVertex;
      indices[indexOffset++] = skirtVertex + 2;
      indices[indexOffset++] = skirtVertex + 1;
      indices[indexOffset++] = skirtVertex + 1;
      indices[indexOffset++] = skirtVertex + 2;
      indices[indexOffset++] = skirtVertex + 3;
      skirtVertex += 4;
    };

    for (let x = 0; x < columns; x += 1) {
      appendSkirt(x, 0, x + 1, 0);
      appendSkirt(x + 1, rows, x, rows);
    }
    for (let y = 0; y < rows; y += 1) {
      appendSkirt(0, y + 1, 0, y);
      appendSkirt(columns, y, columns, y + 1);
    }

    this.vertexArray = requireResource(gl.createVertexArray(), 'vertex array');
    this.vertexBuffer = requireResource(gl.createBuffer(), 'vertex buffer');
    this.indexBuffer = requireResource(gl.createBuffer(), 'index buffer');
    gl.bindVertexArray(this.vertexArray);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 24, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, 24, 20);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    this.indexCount = indices.length;
  }

  private syncSourceMarkers(): void {
    const sourceIds = new Set(this.sources.map((source) => source.id));
    for (const [id, marker] of this.sourceMarkers) {
      if (sourceIds.has(id)) continue;
      marker.remove();
      this.sourceMarkers.delete(id);
    }
    const container = this.map?.getCanvasContainer();
    if (!container) return;
    for (const source of this.sources) {
      if (this.sourceMarkers.has(source.id)) continue;
      const marker = document.createElement('button');
      marker.type = 'button';
      marker.className = 'openflood-source-marker';
      marker.title = `${source.flowRateCubicMetersPerSecond.toFixed(0)} m3/s, radius ${source.radiusMeters.toFixed(0)} m`;
      marker.setAttribute('aria-label', `Inflow at ${source.longitude}, ${source.latitude}`);
      const pin = document.createElement('span');
      pin.className = 'openflood-source-marker__pin';
      const core = document.createElement('span');
      core.className = 'openflood-source-marker__core';
      pin.appendChild(core);
      marker.appendChild(pin);
      container.appendChild(marker);
      this.sourceMarkers.set(source.id, marker);
    }
  }

  private updateSourceMarkers(matrix: Float32Array): void {
    const canvas = this.map?.getCanvas();
    if (!canvas) return;
    const value = (index: number) => matrix[index] ?? 0;
    for (const source of this.sources) {
      const marker = this.sourceMarkers.get(source.id);
      if (!marker) continue;
      const elevation = sampleGridElevation(this.grid, source.longitude, source.latitude);
      const coordinate = maplibregl.MercatorCoordinate.fromLngLat(
        [source.longitude, source.latitude],
        Math.max(0, elevation - this.options.minimumElevationMeters) + 3
      );
      const clipX =
        value(0) * coordinate.x + value(4) * coordinate.y + value(8) * coordinate.z + value(12);
      const clipY =
        value(1) * coordinate.x + value(5) * coordinate.y + value(9) * coordinate.z + value(13);
      const clipW =
        value(3) * coordinate.x + value(7) * coordinate.y + value(11) * coordinate.z + value(15);
      const visible = clipW > 0.000001;
      const safeW = visible ? clipW : 1;
      const screenX = ((clipX / safeW) * 0.5 + 0.5) * canvas.clientWidth;
      const screenY = (0.5 - (clipY / safeW) * 0.5) * canvas.clientHeight;
      marker.hidden = !visible;
      marker.style.transform = `translate(${screenX}px, ${screenY}px) translate(-50%, -100%)`;
    }
  }
}

function sampleGridElevation(grid: DemGrid, longitude: number, latitude: number): number {
  const [west, south, east, north] = grid.bounds;
  const gridX = Math.max(
    0,
    Math.min(grid.width - 1, ((longitude - west) / (east - west)) * (grid.width - 1))
  );
  const gridY = Math.max(
    0,
    Math.min(grid.height - 1, ((north - latitude) / (north - south)) * (grid.height - 1))
  );
  const x0 = Math.floor(gridX);
  const x1 = Math.min(grid.width - 1, x0 + 1);
  const y0 = Math.floor(gridY);
  const y1 = Math.min(grid.height - 1, y0 + 1);
  const fx = gridX - x0;
  const fy = gridY - y0;
  const top =
    (grid.elevations[y0 * grid.width + x0] ?? 0) * (1 - fx) +
    (grid.elevations[y0 * grid.width + x1] ?? 0) * fx;
  const bottom =
    (grid.elevations[y1 * grid.width + x0] ?? 0) * (1 - fx) +
    (grid.elevations[y1 * grid.width + x1] ?? 0) * fx;
  return top * (1 - fy) + bottom * fy;
}

function projectGridPoint(
  grid: DemGrid,
  minimumElevationMeters: number,
  matrix: Float32Array,
  canvas: HTMLCanvasElement,
  x: number,
  y: number
): readonly [number, number] | null {
  const [west, south, east, north] = grid.bounds;
  const longitude = west + (x / (grid.width - 1)) * (east - west);
  const latitude = north - (y / (grid.height - 1)) * (north - south);
  const elevation = grid.elevations[y * grid.width + x] ?? minimumElevationMeters;
  const coordinate = maplibregl.MercatorCoordinate.fromLngLat(
    [longitude, latitude],
    Math.max(0, elevation - minimumElevationMeters)
  );
  return projectMercatorPoint(matrix, canvas, coordinate);
}

function projectMercatorPoint(
  matrix: Float32Array,
  canvas: HTMLCanvasElement,
  coordinate: { readonly x: number; readonly y: number; readonly z: number }
): readonly [number, number] | null {
  const value = (index: number) => matrix[index] ?? 0;
  const clipX =
    value(0) * coordinate.x + value(4) * coordinate.y + value(8) * coordinate.z + value(12);
  const clipY =
    value(1) * coordinate.x + value(5) * coordinate.y + value(9) * coordinate.z + value(13);
  const clipW =
    value(3) * coordinate.x + value(7) * coordinate.y + value(11) * coordinate.z + value(15);
  if (clipW <= 0.000001) return null;
  return [
    ((clipX / clipW) * 0.5 + 0.5) * canvas.clientWidth,
    (0.5 - (clipY / clipW) * 0.5) * canvas.clientHeight
  ];
}

function sameSource(left: InflowSource, right: InflowSource): boolean {
  return (
    left.id === right.id &&
    left.longitude === right.longitude &&
    left.latitude === right.latitude &&
    left.radiusMeters === right.radiusMeters &&
    left.flowRateCubicMetersPerSecond === right.flowRateCubicMetersPerSecond
  );
}

function resolveMainMatrix(context: unknown): Float32Array {
  const value = context as {
    defaultProjectionData?: { mainMatrix?: Float32Array };
    mainMatrix?: Float32Array;
  };
  const matrix = value?.defaultProjectionData?.mainMatrix ?? value?.mainMatrix;
  if (!matrix) throw new Error('MapLibre did not provide a projection matrix to the custom layer.');
  return matrix;
}

function projectOrigin(
  matrix: Float32Array,
  origin: readonly [number, number, number]
): readonly [Float32Array, Float32Array] {
  const value = (index: number) => matrix[index] ?? 0;
  const projected = [
    value(0) * origin[0] + value(4) * origin[1] + value(8) * origin[2] + value(12),
    value(1) * origin[0] + value(5) * origin[1] + value(9) * origin[2] + value(13),
    value(2) * origin[0] + value(6) * origin[1] + value(10) * origin[2] + value(14),
    value(3) * origin[0] + value(7) * origin[1] + value(11) * origin[2] + value(15)
  ];
  const high = Float32Array.from(projected);
  const low = Float32Array.from(projected, (component, index) => component - (high[index] ?? 0));
  return [high, low];
}

function resolveCameraPosition(map: MapLibreMap | null): readonly [number, number, number] {
  const value = map as unknown as {
    transform?: { cameraPosition?: ArrayLike<number> };
  };
  const position = value?.transform?.cameraPosition;
  return [position?.[0] ?? 0, position?.[1] ?? 0, position?.[2] ?? 1];
}

function createProgram(
  gl: WebGL2RenderingContext,
  vertexSource: string,
  fragmentSource: string
): WebGLProgram {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = requireResource(gl.createProgram(), 'program');
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || 'Map layer program link failed.';
    gl.deleteProgram(program);
    throw new Error(message);
  }
  return program;
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = requireResource(gl.createShader(type), 'shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'Map layer shader compilation failed.';
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function requireResource<T>(value: T | null, label: string): T {
  if (!value) throw new Error(`Could not allocate ${label}.`);
  return value;
}
